<template>
  <fragment>
    <AppBody :subtitle="$t('sales.discount-coupon.title')" :actions="actions">
      <CardModule untitle>
        <ExpansionPanelFilters>
          <v-form @submit.prevent="filterCoupons" class="search-forms">
            <v-row>
              <v-col cols="12" md="3" sm="4">
                <BaseTextField
                  :label="$t('sales.discount-coupon.coupon-code')"
                  :placeholder="$t('sales.discount-coupon.insert-coupon-code')"
                  v-model="filter.couponCode"
                  data-cy="codigo"
                ></BaseTextField>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <DateTimePicker
                  :label="$t('sales.discount-coupon.expiration-start-date')"
                  @formatted-date="handleInitDateTime"
                  placeholder="dd/mm/aaaa - hh:mm"
                />
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <DateTimePicker
                  :label="$t('sales.discount-coupon.expiration-finish-date')"
                  @formatted-date="handleEndDateTime"
                  placeholder="dd/mm/aaaa - hh:mm"
                />
              </v-col>
              <v-col cols="12" md="3" sm="4" v-if="isAdmin">
                <BaseSelect
                  v-model="filter.personType"
                  :label="$t('sales.discount-coupon.person-type')"
                  :placeholder="$t('sales.discount-coupon.person-type')"
                  :items="personTypeOptions"
                  item-text="text"
                  item-value="value"
                  data-cy="tipo-pessoa"
                ></BaseSelect>
              </v-col>
              <v-col class="d-flex justify-end">
                <BaseButton variant="filter" data-cy="filtrar" type="submit">
                  {{ $t("common.filter") }}
                </BaseButton>
              </v-col>
            </v-row>
          </v-form>
        </ExpansionPanelFilters>

        <v-data-table
          class="table-body"
          :no-data-text="$t('sales.empty_coupons')"
          :loading-text="$t('sales.loading_coupon_information')"
          :loading="isLoadingCoupons"
          :headers="headersCupom"
          :items="discountCoupons"
          :items-per-page="10"
          :footer-props="{
            itemsPerPageOptions: [10, 20, 50],
            itemsPerPageText: $t('sales.coupons_per_page'),
          }"
        >
          <template v-slot:item.valor_desconto="{ item }">
            {{ item.valor_desconto | moeda }}
          </template>
          <template v-slot:item.perc_desconto="{ item }">
            {{ item.perc_desconto }}%
          </template>
          <template v-slot:item.actions="{ item }">
            <div style="display: flex; justify-content: center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    :color="$config.dados.corinputs"
                    :loading="isLoadingCouponDetails === item.idcupom"
                    @click="editCoupon(item.idcupom)"
                    height="30"
                    width="30"
                    class="mr-2"
                    small
                    icon
                  >
                    <v-icon>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("common.edit") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    :color="$config.dados.corinputs"
                    @click="openDeleteModal(item.idcupom)"
                    height="30"
                    width="30"
                    small
                    icon
                  >
                    <v-icon>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("common.delete") }}</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </CardModule>
    </AppBody>
    <ModalConfirmacao
      :textModal="$t('sales.discount-coupon.delete-confirmation')"
      @callback="deleteCoupon"
    />
  </fragment>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import dayjs from "dayjs";
import CupomMixins from "@/mixins/Cupom.mixins.js";

export default {
  name: "DiscountCoupon",
  metaInfo() {
    const title = this.$t("sales.discount-coupon.title");
    return {
      title: `${title} | ElevenTickets`,
      meta: [
        {
          property: "og:title",
          content: `${title} | ElevenTickets`,
        },
      ],
    };
  },
  components: {
    AppBody: () => import("@/layouts/components/AppBody.vue"),
    CardModule: () => import("@/components/cards/CardModule.vue"),
    BaseTextField: () => import("@/components/forms/BaseTextField.vue"),
    BaseSelect: () => import("@/components/forms/BaseSelect.vue"),
    BaseButton: () => import("@/components/forms/BaseButton.vue"),
    ModalConfirmacao: () => import("@/components/global/modal-confirmacao.vue"),
    ExpansionPanelFilters: () =>
      import("@/components/forms/ExpansionPanelFilters.vue"),
    DateTimePicker: () => import("@/components/forms/DateTimePicker.vue"),
  },
  props: {},
  mixins: [CupomMixins],
  data() {
    return {
      dayjs: dayjs,
      couponId: "",
      filter: {
        couponCode: "",
        initialDate: "",
        finalDate: "",
        personType: "producer",
      },
    };
  },
  computed: {
    ...mapGetters([
      "storeSequencial",
      "storeInfo",
      "isAdmin",
      "discountCoupons",
      "isLoadingCoupons",
      "isLoadingCouponDetails",
    ]),
    actions() {
      return [
        {
          name: this.$t("sales.new_coupon"),
          action: () => this.$cupom_desconto.getCupomByID("", "", "new"),
        },
      ];
    },
    personTypeOptions() {
      return [
        { text: this.$t("sales.discount-coupon.producer"), value: "producer" },
        { text: "E-commerce", value: "ecommerce" },
      ];
    },
    store() {
      return this.filter.personType === "ecommerce" ? -1 : this.storeSequencial;
    },
  },
  methods: {
    ...mapActions([
      "fetchDiscountCoupons",
      "deleteDiscountCoupon",
      "getCouponById",
    ]),
    filterCoupons() {
      this.fetchDiscountCoupons({
        couponCode: this.filter.couponCode,
        initialDate: this.filter.initialDate,
        finalDate: this.filter.finalDate,
        store: this.store,
      });
    },
    handleInitDateTime(date) {
      this.filter.initialDate = date;
    },
    handleEndDateTime(date) {
      this.filter.finalDate = date;
    },
    async editCoupon(couponId) {
      await this.getCouponById(couponId);
      this.$router.push({
        name: "discountCouponSettings",
        params: {
          type: "editar",
        },
      });
    },
    openDeleteModal(couponId) {
      this.$config.modalConfirm = true;
      this.couponId = couponId;
    },
    deleteCoupon() {
      this.deleteDiscountCoupon({ couponId: this.couponId, store: this.store });
    },
  },
  mounted() {
    this.filterCoupons();
  },
  destroyed() {
    this.couponId = "";
    this.filter = {
      couponCode: "",
      initialDate: "",
      finalDate: "",
      personType: "producer",
    };
  },
};
</script>

<style scoped></style>
